'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink, splitLink, unstable_httpSubscriptionLink } from '@trpc/client';
import { useState } from 'react';
import superjson from 'superjson';
import { trpc } from './client';

function getBaseUrl() {
  if (typeof window !== 'undefined') return ''; // browser should use relative path
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
  return `http://localhost:${process.env.PORT ?? 3000}`;
}

export function TRPCProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
      },
    },
  }));
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        splitLink({
          // Use SSE for subscriptions
          condition: (op) => op.type === 'subscription',
          true: unstable_httpSubscriptionLink({
            url: `${getBaseUrl()}/api/trpc`,
            transformer: superjson,
          }),
          // Use HTTP batch for queries and mutations
          false: httpBatchLink({
            url: `${getBaseUrl()}/api/trpc`,
            transformer: superjson,
            fetch(url, options) {
              return fetch(url, {
                ...options,
                credentials: 'include', // Send cookies with requests for Clerk authentication
              }).then(async (response) => {
                // Clone response to read body without consuming it
                const clonedResponse = response.clone();
                
                // Log non-JSON responses for debugging
                if (!response.headers.get('content-type')?.includes('application/json')) {
                  const text = await clonedResponse.text();
                  console.error('[tRPC] Non-JSON response:', {
                    url,
                    status: response.status,
                    statusText: response.statusText,
                    contentType: response.headers.get('content-type'),
                    body: text.substring(0, 500),
                    method: options?.method || 'GET',
                  });
                  
                  // If it's an empty response, provide more context
                  if (!text || text.trim() === '') {
                    console.error('[tRPC] Empty response body - possible causes:', {
                      url,
                      status: response.status,
                      'Possible causes': [
                        'Route handler returned undefined',
                        'Middleware intercepted request',
                        'Database connection issue',
                        'Context creation failed',
                      ],
                    });
                  }
                  
                  throw new Error(`tRPC endpoint returned non-JSON response (${response.status}): ${text.substring(0, 100) || 'empty response'}`);
                }
                return response;
              }).catch((error) => {
                console.error('[tRPC] Fetch error:', {
                  url,
                  error: error.message,
                  stack: error.stack,
                });
                throw error;
              });
            },
          }),
        }),
      ],
    })
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
